import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useInvoicePermissions } from '../useInvoicePermissions';
import { useUser } from '../../../contexts/UserContext';

const useInvoiceApproval = (
  invoice,
  onApprove,
  onReject,
  initiateApproval,
  onTransferApprover,
  updateInvoiceOptimistically = null
) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showTransferSection, setShowTransferSection] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [isProcessingInternal, setIsProcessingInternal] = useState(false);

  const queryClient = useQueryClient();
  const { canApproveOrReject } = useInvoicePermissions();
  const { user } = useUser();

  const notifyStatusChanged = (invoiceId, newStatus) => {
    const event = new CustomEvent('invoice-status-changed', {
      detail: { invoiceId, newStatus }
    });
    window.dispatchEvent(event);
    queryClient.invalidateQueries(['spInvoices']);
    queryClient.invalidateQueries(['approvalPackages']);
  };

  const handleApprove = async () => {
    try {
      if (!canApproveOrReject()) {
        alert('אין לך הרשאה לאשר חשבוניות');
        return;
      }

      const canApproveThisInvoice = invoice.approvalPackage?.canApprove === true;
      if (!canApproveThisInvoice) {
        alert('אין לך הרשאה לאשר חשבונית זו');
        return;
      }

      if (!invoice.approvalPackage || !invoice.approvalPackage._id) {
        alert('לא נמצאה חבילת אישור לחשבונית זו');
        return;
      }

      const validStatuses = ['ממתין לאישור', 'REQUIRES_LINE_MAPPING', 'PENDING_CLARIFICATION'];
      if (!validStatuses.includes(invoice.status)) {
        alert('החשבונית אינה במצב המתאים לאישור');
        return;
      }

      initiateApproval(invoice);

    } catch (error) {
      console.error('Error in handleApprove:', error);
      alert(`שגיאה באישור החשבונית: ${error.message || 'שגיאה לא ידועה'}`);
      notifyStatusChanged(invoice.id, 'ממתין לאישור');
      setIsProcessingInternal(false);
    }
  };

  const handleReject = async () => {
    try {
      if (!canApproveOrReject()) {
        alert('אין לך הרשאה לדחות חשבוניות');
        return;
      }

      if (!rejectionReason.trim()) {
        alert('נדרשת סיבת דחייה');
        return;
      }

      if (!invoice.approvalPackage || !invoice.approvalPackage._id) {
        alert('לא נמצאה חבילת אישור לחשבונית זו');
        return;
      }

      const validStatuses = ['ממתין לאישור', 'REQUIRES_LINE_MAPPING', 'PENDING_CLARIFICATION'];
      if (!validStatuses.includes(invoice.status)) {
        alert('החשבונית אינה במצב המתאים לדחייה');
        return;
      }

      const rejectionData = {
        apId: invoice.approvalPackage._id,
        rejectionReason: rejectionReason,
        rejectionText: rejectionReason
      };

      setIsProcessingInternal(true);
      notifyStatusChanged(invoice.id, 'נדחה');
      if (updateInvoiceOptimistically) {
        updateInvoiceOptimistically(invoice.id, {
          status: 'נדחה',
          rejectionReason: rejectionReason
        });
      }

      setTimeout(() => setIsProcessingInternal(false), 100);

      onReject(invoice.id, rejectionData).catch(error => {
        console.error('Background rejection error:', error);
      });

      setRejectionReason('');

    } catch (error) {
      console.error('Error in handleReject:', error);
      alert(`שגיאה בדחיית החשבונית: ${error.message || 'שגיאה לא ידועה'}`);
      notifyStatusChanged(invoice.id, 'ממתין לאישור');
      setIsProcessingInternal(false);
    }
  };

  const handleTransferApprover = async () => {
    try {
      if (!canApproveOrReject()) {
        alert('אין לך הרשאה להעביר מאשר');
        return;
      }

      if (!selectedApprover) {
        alert('נדרש לבחור מאשר');
        return;
      }

      if (!invoice.approvalPackage || !invoice.approvalPackage._id) {
        alert('לא נמצאה חבילת אישור לחשבונית זו');
        return;
      }

      const validStatuses = ['ממתין לאישור', 'REQUIRES_LINE_MAPPING', 'PENDING_CLARIFICATION'];
      if (!validStatuses.includes(invoice.status)) {
        alert('החשבונית אינה במצב המתאים להעברת מאשר');
        return;
      }

      const transferData = {
        apId: invoice.approvalPackage._id,
        newApproverId: selectedApprover,
        invoiceId: invoice.id
      };

      setSelectedApprover('');
      setShowTransferSection(false);

      onTransferApprover(transferData).catch(error => {
        console.error('Background transfer error:', error);
      });

    } catch (error) {
      console.error('Error in handleTransferApprover:', error);
      alert(`שגיאה בהעברת המאשר: ${error.message || 'שגיאה לא ידועה'}`);
      throw error;
    }
  };

  const toggleTransferSection = () => {
    setShowTransferSection(!showTransferSection);
    if (!showTransferSection) {
      setSelectedApprover('');
    }
  };

  const isCurrentActiveApprover = () => {
    const userPriorityName = user?.priorityUserName || user?.PriorityUserName;

    if (!invoice?.approvalPackage?.approversList || !userPriorityName) {
      return false;
    }

    const activeApprover = invoice.approvalPackage.approversList.find(
      approver => !approver.approve
    );

    if (!activeApprover) {
      return false;
    }

    return activeApprover.userName === userPriorityName?.trim();
  };

  const userHasApprovalPermission = canApproveOrReject();
  const canApproveThisInvoice = invoice?.approvalPackage && invoice.approvalPackage?.canApprove === true;

  const hasErrorDetails = !!invoice?.errorDetails;

  const validApprovalStatuses = ['ממתין לאישור', 'REQUIRES_LINE_MAPPING', 'PENDING_CLARIFICATION'];
  const canTransferApprover = userHasApprovalPermission &&
    validApprovalStatuses.includes(invoice?.status) &&
    invoice?.approvalPackage?._id &&
    isCurrentActiveApprover();

  return {
    rejectionReason,
    setRejectionReason,
    handleApprove,
    handleReject,
    userHasApprovalPermission,
    canApproveThisInvoice,
    hasErrorDetails,
    showTransferSection,
    setShowTransferSection,
    selectedApprover,
    setSelectedApprover,
    handleTransferApprover,
    toggleTransferSection,
    canTransferApprover,
    isProcessingInternal
  };
};

export default useInvoiceApproval;